/**
 * React Query hooks for BOX API
 *
 * Features:
 * - useQuery for status and history
 * - useMutation for upload and cancel operations
 * - Polling for upload progress
 * - Automatic cache invalidation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../lib/api';
import type { BoxStatusResponse, BoxUploadInput } from '../types/api';

// ─── Query Keys ───

const BOX_KEYS = {
  all: ['box'] as const,
  status: (uploadId: string) => [...BOX_KEYS.all, 'status', uploadId] as const,
  history: (page: number, pageSize: number) =>
    [...BOX_KEYS.all, 'history', { page, pageSize }] as const,
};

// ─── Upload Hooks ───

/**
 * Upload file with metadata
 */
export function useUploadFile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: BoxUploadInput) => getApiClient().uploadFile(input),
    onSuccess: () => {
      // Invalidate history to show new upload
      queryClient.invalidateQueries({ queryKey: BOX_KEYS.all });
    },
  });
}

// ─── Status Hooks ───

/**
 * Get upload status with optional polling
 *
 * @param uploadId - Upload ID to track
 * @param options.enabled - Enable/disable query
 * @param options.refetchInterval - Polling interval (ms), or function to determine interval
 */
export function useUploadStatus(
  uploadId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  const defaultInterval = typeof options?.refetchInterval === 'number' ? options.refetchInterval : 2000;

  const query = useQuery({
    queryKey: BOX_KEYS.status(uploadId || ''),
    queryFn: () => getApiClient().getUploadStatus(uploadId!),
    enabled: !!uploadId && (options?.enabled ?? true),
    // Stop polling when status is final (completed/failed/cancelled)
    refetchInterval: (query) => {
      if (options?.refetchInterval === false) return false;
      const status = query.state.data?.status;
      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        return false; // Stop polling
      }
      // Adaptive interval: slower for pending, faster for processing
      return status === 'processing' ? defaultInterval : defaultInterval * 2;
    },
  });

  // Helper to check if polling has stopped
  const isFinalStatus =
    query.data?.status === 'completed' ||
    query.data?.status === 'failed' ||
    query.data?.status === 'cancelled';

  return {
    ...query,
    isFinalStatus,
  };
}

// ─── History Hooks ───

/**
 * Get upload history with pagination
 */
export function useUploadHistory(workspaceId: string, page = 1, pageSize = 20) {
  return useQuery({
    queryKey: [...BOX_KEYS.history(page, pageSize), workspaceId],
    queryFn: () => getApiClient().getUploadHistory(workspaceId, page, pageSize),
    staleTime: 1000 * 30, // 30 seconds
    enabled: workspaceId.length > 0,
  });
}

// ─── Cancel Hooks ───

/**
 * Cancel pending upload
 */
export function useCancelUpload() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (uploadId: string) => getApiClient().cancelUpload(uploadId),
    onSuccess: (_, uploadId) => {
      // Invalidate status query to refresh
      queryClient.invalidateQueries({ queryKey: BOX_KEYS.status(uploadId) });

      // Invalidate history to update list
      queryClient.invalidateQueries({ queryKey: BOX_KEYS.all });
    },
  });
}

// ─── Combined Upload Hook with Status Tracking ───

/**
 * Combined hook for upload with automatic status tracking
 *
 * Usage:
 * ```tsx
 * const { upload, statusData, isUploading, cancel } = useUploadWithTracking();
 *
 * const handleUpload = async (metadata: FileUploadMetadata) => {
 *   const response = await upload(metadata);
 *   // Status is automatically tracked
 * };
 * ```
 */
export function useUploadWithTracking() {
  const upload = useUploadFile();
  const cancel = useCancelUpload();

  // Track latest upload ID
  const uploadId = upload.data?.uploadId || null;

  // Automatically track status
  const statusQuery = useUploadStatus(uploadId, {
    enabled: !!uploadId,
  });

  return {
    upload: upload.mutateAsync,
    statusData: statusQuery.data,
    isUploading: upload.isPending,
    isTracking: statusQuery.isFetching,
    cancel: cancel.mutateAsync,
    error: upload.error || cancel.error,
    uploadResponse: upload.data,
  };
}

// ─── Utility Functions ───

/**
 * Check if upload is in progress
 */
export function isUploadInProgress(status?: BoxStatusResponse): boolean {
  return status?.status === 'pending' || status?.status === 'processing';
}

/**
 * Check if upload is completed
 */
export function isUploadCompleted(status?: BoxStatusResponse): boolean {
  return status?.status === 'completed';
}

/**
 * Check if upload has failed
 */
export function isUploadFailed(status?: BoxStatusResponse): boolean {
  return status?.status === 'failed';
}

/**
 * Get upload progress percentage
 */
export function getUploadProgress(status?: BoxStatusResponse): number {
  return status?.progress ?? 0;
}
